import {
  ArrowLeftIcon,
  FileTextIcon,
  InfoIcon,
  ListChecksIcon,
  WrenchIcon,
} from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { Outlet, useLocation, useParams } from 'react-router-dom'

import { useGetUnit } from '@/api/units/get-unit'
import { Page } from '@/components/layout/page'
import { PageHeader } from '@/components/layout/page-header'
import { ProfileSidebar } from '@/components/layout/profile-sidebar'
import { SideNavRouteItem } from '@/components/layout/side-nav'
import { Badge } from '@/components/ui/badge'
import { ErrorMessage } from '@/components/ui/error-message'
import { Icon } from '@/components/ui/icon'
import { Link } from '@/components/ui/link'

import {
  getLeaseStatusColor,
  getLeaseStatusLabel,
  getUnitStatusColor,
  getUnitStatusLabel,
} from '../units.page.utils'
import * as S from './unit-details.page.styled'

const TABLE_TABS = ['leases', 'maintenance', 'checklists']

export const UnitDetailsPage = () => {
  const { t } = useTranslation(['units', 'common'])
  const { unitId } = useParams<{ unitId: string }>()

  const { data: unit, isError } = useGetUnit({ unitId: unitId! })
  const { pathname } = useLocation()
  const isTableTab = TABLE_TABS.some((tab) => pathname.endsWith(`/${tab}`))

  const title = t('units:detail.title', {
    buildingNumber: unit?.buildingNumber ?? '',
    unitNumber: unit?.unit ?? '',
  }).trim()

  const navItems: SideNavRouteItem[] = [
    {
      icon: InfoIcon,
      label: t('units:detail.tabs.general'),
      to: { params: { unitId: unitId! }, path: '/dashboard/units/:unitId/general' },
    },
    {
      icon: FileTextIcon,
      label: t('units:detail.tabs.leases'),
      to: { params: { unitId: unitId! }, path: '/dashboard/units/:unitId/leases' },
    },
    {
      icon: WrenchIcon,
      label: t('units:detail.tabs.maintenance'),
      to: { params: { unitId: unitId! }, path: '/dashboard/units/:unitId/maintenance' },
    },
    {
      icon: ListChecksIcon,
      label: t('units:detail.tabs.tasks'),
      to: { params: { unitId: unitId! }, path: '/dashboard/units/:unitId/checklists' },
    },
  ]

  return (
    <Page fillHeight={isTableTab}>
      <PageHeader
        title={t('units:detail.profileTitle')}
        backButton={
          <Link size="small" use="secondary" to={{ path: '/dashboard/units' }}>
            <Icon icon={ArrowLeftIcon} size="1.4rem" />
            {t('common:actions.back')}
          </Link>
        }
      />
      <S.Layout $fixedHeight={isTableTab}>
        <ProfileSidebar
          title={title}
          ariaLabel={t('units:detail.profileTitle')}
          items={navItems}
          badges={
            unit && (
              <>
                <Badge
                  color={getUnitStatusColor(unit.occupancyStatus)}
                  size="small"
                  variant="light"
                >
                  {getUnitStatusLabel(unit.occupancyStatus, t)}
                </Badge>
                {unit.currentLease?.leaseStatus === 'Current' && (
                  <Badge
                    color={getLeaseStatusColor(unit.currentLease.leaseStatus)}
                    size="small"
                    variant="light"
                  >
                    {getLeaseStatusLabel(unit.currentLease.leaseStatus, t)}
                  </Badge>
                )}
              </>
            )
          }
        />
        <S.Content>
          {isError ? <ErrorMessage>{t('units:detail.general.error')}</ErrorMessage> : <Outlet />}
        </S.Content>
      </S.Layout>
    </Page>
  )
}
